import { useState, useEffect } from 'react'
import {
  Button,
  Dialog,
  DialogBody,
  DialogFooter,
  DialogHeader
} from '@material-tailwind/react'

export const ZipDownloadRangeDialog = ({
  open,
  onClose,
  onConfirm,
  isLoading = false,
  title = 'Descargar documentos por período',
  conflict = null
}) => {
  const [fromMonth, setFromMonth] = useState('')
  const [toMonth, setToMonth] = useState('')

  // 'form' | 'success'
  const [step, setStep] = useState('form')

  // Internal control for opening the main modal
  const [internalOpen, setInternalOpen] = useState(open)

  // Validation error modal
  const [errorModal, setErrorModal] = useState({
    open: false,
    message: ''
  })

  const showError = (message) => {
    setErrorModal({ open: true, message })
  }

  const closeError = () => {
    setErrorModal({ open: false, message: '' })
  }

  const resetState = () => {
    setFromMonth('')
    setToMonth('')
    setStep('form')
  }

  const handleClose = () => {
    if (isLoading) return
    setInternalOpen(false)
    resetState()
    onClose?.()
  }

  // React only when the parent opens the modal.
  // Ignore external "open = false" so closing happens only via buttons.
  useEffect(() => {
    if (open) {
      setInternalOpen(true)
      resetState()
    }
  }, [open])

  const formatHumanDate = (isoString) => {
    if (!isoString) return ''

    const date = new Date(isoString)

    return date.toLocaleString('es-CL', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  const formatMonthPeriod = (fromMonthValue, toMonthValue) => {
    if (!fromMonthValue || !toMonthValue) return ''

    const [fy, fm] = fromMonthValue.split('-')
    const [ty, tm] = toMonthValue.split('-')

    const fromDate = new Date(Number(fy), Number(fm) - 1, 1)
    const toDate = new Date(Number(ty), Number(tm) - 1, 1)

    const fromStr = fromDate.toLocaleDateString('es-CL', {
      month: 'long',
      year: 'numeric'
    })
    const toStr = toDate.toLocaleDateString('es-CL', {
      month: 'long',
      year: 'numeric'
    })

    if (fromStr === toStr) return fromStr
    return `${fromStr} a ${toStr}`
  }

  const handleConfirm = () => {
    if (!fromMonth || !toMonth) {
      showError('Debes seleccionar el mes "desde" y el mes "hasta".')
      return
    }

    const [fromYear, fromMonthNumStr] = fromMonth.split('-')
    const [toYear, toMonthNumStr] = toMonth.split('-')

    const fromYearNum = Number(fromYear)
    const fromMonthNum = Number(fromMonthNumStr)
    const toYearNum = Number(toYear)
    const toMonthNum = Number(toMonthNumStr)

    // Basic validation: from <= to
    if (
      toYearNum < fromYearNum ||
      (toYearNum === fromYearNum && toMonthNum < fromMonthNum)
    ) {
      showError('El mes "hasta" debe ser posterior o igual al mes "desde".')
      return
    }

    // from_date = first day of the "from" month
    const fromDate = `${fromYearNum}-${String(fromMonthNum).padStart(2, '0')}-01`

    // to_date = last day of the "to" month
    const lastDayOfToMonth = new Date(toYearNum, toMonthNum, 0).getDate()
    const toDate = `${toYearNum}-${String(toMonthNum).padStart(2, '0')}-${String(
      lastDayOfToMonth
    ).padStart(2, '0')}`

    onConfirm?.({
      fromDate,
      toDate,
      fromMonth,
      toMonth
    })

    // Switch from "form" to "confirmation"
    setStep('success')
  }

  const handleSuccessAccept = () => {
    // Close modal and reset only when the user decides
    handleClose()
  }

  const conflictOpen = conflict?.open
  const conflictExport = conflict?.existingExport || {}
  const conflictIsLoading = conflict?.isLoading || false

  return (
    <>
      {/* Main modal: shows form or success message based on step */}
      <Dialog
        size='sm'
        open={internalOpen}
        handler={handleClose}
      >
        <DialogHeader>
          {step === 'form' ? title : 'Solicitud enviada'}
        </DialogHeader>

        <DialogBody>
          {step === 'form' && (
            <>
              <p className='text-sm text-gray-700 mb-4 leading-relaxed'>
                Selecciona el rango de meses para generar el archivo ZIP.{' '}
                El período se interpreta desde el <strong>primer día</strong> del mes inicial
                hasta el <strong>último día</strong> del mes final.
                <br /><br />
                El archivo generado se enviará por correo y estará disponible por{' '}
                <strong>7 días</strong>.
              </p>

              <div className='flex flex-col gap-4 sm:flex-row mt-4'>
                <div className='flex-1'>
                  <label className='block text-xs font-medium text-gray-700 mb-1'>
                    Desde (mes)
                  </label>
                  <input
                    type='month'
                    className='border rounded px-2 py-1 text-sm w-full'
                    value={fromMonth}
                    onChange={(e) => setFromMonth(e.target.value)}
                  />
                </div>

                <div className='flex-1'>
                  <label className='block text-xs font-medium text-gray-700 mb-1'>
                    Hasta (mes)
                  </label>
                  <input
                    type='month'
                    className='border rounded px-2 py-1 text-sm w-full'
                    value={toMonth}
                    onChange={(e) => setToMonth(e.target.value)}
                  />
                </div>
              </div>
            </>
          )}

          {step === 'success' && (
            <div className='space-y-3'>
              <p className='text-sm text-gray-700 leading-relaxed'>
                Se está generando el archivo ZIP con los documentos del período:
              </p>

              {fromMonth && toMonth && (
                <p className='text-sm font-medium text-gray-900'>
                  {formatMonthPeriod(fromMonth, toMonth)}
                </p>
              )}

              <p className='text-sm text-gray-700 leading-relaxed'>
                Recibirás un correo con el enlace de descarga cuando el proceso termine.
                El archivo estará disponible durante <strong>7 días</strong>.
              </p>
            </div>
          )}
        </DialogBody>

        <DialogFooter className='flex gap-3'>
          {step === 'form' ? (
            <>
              <Button
                variant='text'
                color='gray'
                onClick={handleClose}
                disabled={isLoading}
              >
                Cancelar
              </Button>

              <Button
                onClick={handleConfirm}
                disabled={isLoading}
              >
                {isLoading ? 'Generando ZIP…' : 'Confirmar descarga'}
              </Button>
            </>
          ) : (
            <Button
              color='blue'
              onClick={handleSuccessAccept}
            >
              Aceptar
            </Button>
          )}
        </DialogFooter>
      </Dialog>

      {/* Conflict modal (ZIP already exists) */}
      {conflict && (
        <Dialog
          size='sm'
          open={!!conflictOpen}
          handler={conflict.onCancel}
        >
          <DialogHeader>ZIP ya generado recientemente</DialogHeader>

          <DialogBody>
            <p className='text-sm text-gray-700 mb-4 leading-relaxed'>
              Ya existe un archivo ZIP generado recientemente para este rango de fechas.
              Puedes reutilizar el archivo actual o generar uno nuevo.
            </p>

            {(conflictExport.generated_at || conflictExport.expires_at) && (
              <div className='text-xs text-gray-600 bg-gray-50 rounded-md px-3 py-2 space-y-1'>
                {conflictExport.generated_at && (
                  <p>
                    <span className='font-semibold'>Generado el:</span>{' '}
                    {formatHumanDate(conflictExport.generated_at)}
                  </p>
                )}
                {conflictExport.expires_at && (
                  <p>
                    <span className='font-semibold'>Disponible hasta:</span>{' '}
                    {formatHumanDate(conflictExport.expires_at)}
                  </p>
                )}
              </div>
            )}

            <p className='text-xs text-gray-600 mt-4'>
              Si decides generar un nuevo archivo, el ZIP anterior será eliminado y se
              creará uno nuevo con la información actual del período seleccionado.
            </p>
          </DialogBody>

          <DialogFooter className='flex gap-3'>
            <Button
              variant='text'
              color='gray'
              onClick={conflict.onCancel}
              disabled={conflictIsLoading}
            >
              Usar ZIP existente
            </Button>

            <Button
              onClick={conflict.onConfirm}
              disabled={conflictIsLoading}
            >
              {conflictIsLoading ? 'Generando nuevo ZIP…' : 'Generar nuevo ZIP'}
            </Button>
          </DialogFooter>
        </Dialog>
      )}

      {/* Validation error modal */}
      <Dialog size='sm' open={errorModal.open} handler={closeError}>
        <DialogHeader>Error en los datos ingresados</DialogHeader>

        <DialogBody>
          <p className='text-sm text-gray-700'>{errorModal.message}</p>
        </DialogBody>

        <DialogFooter>
          <Button variant='filled' color='blue' onClick={closeError}>
            Entendido
          </Button>
        </DialogFooter>
      </Dialog>
    </>
  )
}
